import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useExport } from './header/exporters';
import { localDayKey } from '../utils/localDate';
import { getAppLocale } from '../utils/locale';
import TagMultiSelect from './ui/TagMultiSelect';
import { UnconvertedMark, UnconvertedNote } from './ui/UnconvertedMark';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiGet, planningPath } from '../services/PlanningApiClient';
import { Input as FormInput } from './FormField';
import RichTextEditor from './ui/RichTextEditor';
import EmptyState from './EmptyState';
import ConfirmDialog from './ui/ConfirmDialog';
import SearchableSelect from './ui/SearchableSelect';
import { BudgetCategory, VendorBudgetItem, Payment } from '../types/ui/Budget';
import { VendorQuote } from '../types/ui/VendorQuote';
import { VendorBookingStatus } from '../types/Vendor';
import { VENDOR_BOOKING_STATUS_LABELS, VENDOR_BOOKING_STATUS_COLORS } from '../utils/vendorBookingStatus';
import { ParsedVendor } from '../data/vendorData';
import { calculateBudgetSummary, refreshBudgetFromApi, apiUpdateBudgetItem, apiDeleteBudgetItem, apiCreatePayment, netPaid } from '../utils/budgetStorage';
import { loadContributors } from '../services/ContributorService';
import { Contributor } from '../types/ui/Settlement';
import { StoredEvent, loadEvents, refreshEventsFromApi } from '../services/EventsService';
import { createQuote, loadQuotes, acceptQuote, rejectQuote } from '../services/VendorQuoteService';
import {
  VENDOR_BOOKINGS_UPDATED_EVENT,
  refreshBookingsFromApi,
  loadBookings,
} from '../services/VendorBookingService';
import CostPerHeadPanel from './CostPerHeadPanel';
import { MetricCard, ProgressMetricCard } from './MetricCards';
import PageHeader from './PageHeader';
import DataTable, { actionsColumn, Column } from './DataTable';
import StatusBadge, { StatusBadgeVariant } from './ui/StatusBadge';
import { formatDate } from '../utils/formatDate';
import { Modal } from './ui/Modal';
import { MaskedValue } from './ui/MaskedValue';
import { QuoteFormFields, QuoteFormValues } from './QuoteFormFields';
import '../styles/components/BudgetTracker.css';
import { uploadImage, getAttachments, deleteAttachment, resolveMediaUrl, type Attachment } from '../services/uploadService';
import { useNotifications } from './NotificationProvider';
import { useSettings } from '../contexts/SettingsContext';
import LoadErrorBox from './ui/LoadErrorBox';
import { convertCurrency, currencySymbol, isCurrencySet, type CurrencyCode } from '../utils/currency';
import BudgetChart from './BudgetChart';
import { fmtId } from '../utils/jiraId';
import RecordActivity from './ui/RecordActivity';
import { RecordLabelText } from './ui/idColumn';
import { usePublishRecordList } from '../hooks/useRecordList';

const EMPTY_VENDORS: ParsedVendor[] = [];

// The "what did this line really cost" fallback chain — actual cost, else
// planned/quoted cost, else quantity × unit price — appeared inline (and
// currency-unaware) in several places on this page. NGN-converted via each
// field's own *_NGN mirror (see budgetStorage.transformApiItem); quantity ×
// unit_cost is assumed to be in the item's own currency, same as
// planned_cost/actual_cost, so it gets the same fxRate treatment.
function vendorGrossCostBase(v: VendorBudgetItem): number {
  // != null, not truthiness: a real ₦0 actual cost (a comped invoice, say)
  // is a genuine fact and must not fall through to planned_cost as if it
  // were never recorded.
  // The line's cost is its planned_cost (what it is expected to cost); actual_cost is what has been paid.
  if (v.plannedCostBase != null) return v.plannedCostBase;
  if (v.quantity && v.unit_cost) return v.quantity * v.unit_cost; // already in the budget's currency
  return 0;
}

const PAYMENT_DS: Record<string, string> = {
  completed: 'success',
  pending: 'warning',
  failed: 'danger',
};

interface BudgetTrackerProps {
  vendors?: ParsedVendor[];
  shippingImportData?: {
    amount: number;
    description: string;
    currency: CurrencyCode;
  } | null;
  onShippingImported?: () => void;
  eventFilter?: string;
  onEventFilterChange?: (id: string) => void;
  budgetView?: 'cards' | 'table';
  onBudgetViewChange?: (view: 'cards' | 'table') => void;
  triggerAddCategory?: boolean;
  onAddCategoryHandled?: () => void;
  categoryId?: string;
  budgetCategoryTab?: 'overview' | 'vendors' | 'purchases' | 'payments';
  onBudgetCategoryTabChange?: (tab: 'overview' | 'vendors' | 'payments') => void;
  triggerAddBudgetVendor?: boolean;
  onAddBudgetVendorHandled?: () => void;
  triggerSaveBudgetCategory?: boolean;
  onSaveBudgetCategoryHandled?: () => void;
  onVendorEditStateChange?: (editing: boolean) => void;
  triggerSaveBudgetVendor?: boolean;
  onSaveBudgetVendorHandled?: () => void;
  triggerCancelBudgetVendor?: boolean;
  onCancelBudgetVendorHandled?: () => void;
}

/** Priority is stored as a slug ('must-have'); these are the human labels,
 *  the same wording the edit form's own options use. The badge rendered the
 *  raw slug, so a lower-case hyphenated token sat beside a properly titled
 *  event badge. */
const PRIORITY_LABEL: Record<string, string> = {
  'must-have': 'Must have',
  'nice-to-have': 'Nice to have',
  'cut-if-needed': 'Cut if needed',
};

const PRIORITY_VARIANT: Record<string, StatusBadgeVariant> = {
  'must-have': 'warning',
  'nice-to-have': 'info',
  'cut-if-needed': 'danger',
};

/** Red/Amber/Green read on spend-vs-plan: comfortable margin, closing in,
 *  over. Same 80% amber threshold as a rough "getting close" convention
 *  elsewhere in budgeting UIs — there's no stored threshold to read this
 *  from, so it's a judgment call, not a derived fact. */
const ragLevel = (percentUsed: number): 'green' | 'amber' | 'red' =>
  percentUsed > 100 ? 'red' : percentUsed >= 80 ? 'amber' : 'green';

interface CategoryOrder {
  id: string; status: string; marketplace: string; notes: string | null;
  total_amount_cents: number; currency: string; order_date: string | null;
  vendor_name?: string | null;
}

/** Shared by the Overview tab's own Purchase Orders section and the
 *  Purchases tab's full table — a purchase is its own record type with its
 *  own lifecycle (draft → ordered → shipped → delivered) and its own
 *  columns, not a vendor booking status stretched to fit. Defined once so
 *  the two don't drift the way Vendor and Quotes' column sets did before
 *  being reconciled. */
const PURCHASE_ORDER_COLUMNS: Column<CategoryOrder>[] = [
  {
    key: 'id',
    header: 'Order',
    render: (_v, o) => <strong><RecordLabelText kind="procurementOrder" id={o.id} /></strong>,
  },
  {
    key: 'item',
    header: 'Item',
    render: (_v, o) => (o.notes ?? '').split('\n')[0].slice(0, 70) || o.vendor_name || '—',
  },
  {
    key: 'marketplace',
    header: 'Source',
    render: (_v, o) => o.marketplace || '—',
  },
  {
    key: 'total',
    header: 'Cost',
    render: (_v, o) => {
      return `${currencySymbol(o.currency)}${(o.total_amount_cents / 100).toLocaleString(getAppLocale())}`;
    },
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, o) => <span className="ds-badge ds-badge-info">{o.status.replace(/-/g, ' ')}</span>,
  },
];

const BudgetTracker: React.FC<BudgetTrackerProps> = ({ vendors = EMPTY_VENDORS, shippingImportData, onShippingImported, eventFilter: externalEventFilter, onEventFilterChange, budgetView: externalBudgetView, onBudgetViewChange, triggerAddCategory, onAddCategoryHandled, categoryId, budgetCategoryTab: externalCategoryTab, onBudgetCategoryTabChange, triggerAddBudgetVendor, onAddBudgetVendorHandled, triggerSaveBudgetCategory, onSaveBudgetCategoryHandled, onVendorEditStateChange, triggerSaveBudgetVendor, onSaveBudgetVendorHandled, triggerCancelBudgetVendor, onCancelBudgetVendorHandled }) => {
  const { settings, getCurrencySymbol, formatCurrency, formatCurrencyCompact } = useSettings();
  const { showSuccess, showError } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const contractFileRef = useRef<HTMLInputElement>(null);
  const [contractUploadVendorId, setContractUploadVendorId] = useState<string | null>(null);
  const [contractUploading, setContractUploading] = useState(false);
  const [vendorContracts, setVendorContracts] = useState<Record<string, Attachment[]>>({});
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [quotes, setQuotes] = useState<VendorQuote[]>([]);
  // Procurement orders belonging to this category. They carry
  // budget_category_id, so a purchase can exist with no budget line item at
  // all — those were invisible on this page, which made the category look
  // cheaper than it is.
  const [categoryOrders, setCategoryOrders] = useState<CategoryOrder[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showGlobalPaymentModal, setShowGlobalPaymentModal] = useState(false);
  const [globalPaymentVendorId, setGlobalPaymentVendorId] = useState('');
  const [internalBudgetView, setInternalBudgetView] = useState<'cards' | 'table'>('cards');
  const viewMode = externalBudgetView ?? internalBudgetView;
  const setViewMode = (v: 'cards' | 'table') => { setInternalBudgetView(v); onBudgetViewChange?.(v); };
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<BudgetCategory | null>(null);
  const [internalEventFilter, setInternalEventFilter] = useState<string>('');
  const eventFilter = externalEventFilter ?? internalEventFilter;
  const setEventFilter = (id: string) => { setInternalEventFilter(id); onEventFilterChange?.(id); };
  const [events, setEvents] = useState<StoredEvent[]>([]);
  const [categoryTab, setCategoryTab] = useState<'overview' | 'vendors' | 'purchases' | 'payments'>(externalCategoryTab ?? 'vendors');
  const [isLoading, setIsLoading] = useState(true);
  const [focusedAmountField, setFocusedAmountField] = useState<'planned' | 'actual' | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [editingVendorId, setEditingVendorId] = useState<string | null>(null);
  const [vendorEditDraft, setVendorEditDraft] = useState<Partial<VendorBudgetItem>>({});
  const addVendorSubmitRef = React.useRef<(() => void) | null>(null);
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);
    void Promise.all([refreshBudgetFromApi(), refreshBookingsFromApi()]).then(([cats]) => {
      // Vendor-booking-derived lines are real budget_items rows (synced
      // server-side by the vendor-bookings API), so the plain category
      // fetch already includes them — no client-side reconstruction needed.
      setCategories(cats);
      setIsLoading(false);
    }).catch((err: unknown) => {
      setLoadError(err ?? new Error('load failed'));
      setIsLoading(false);
    });

    // A booking save elsewhere (VendorBookingManager, etc.) has already
    // updated its linked budget_items row server-side by the time this
    // event fires — just re-fetch to pick it up.
    const refreshBudgetFromBookings = () => {
      void refreshBudgetFromApi().then(setCategories).catch(() => {});
    };
    window.addEventListener(VENDOR_BOOKINGS_UPDATED_EVENT, refreshBudgetFromBookings as EventListener);
    window.addEventListener('storage', refreshBudgetFromBookings);

    return () => {
      window.removeEventListener(VENDOR_BOOKINGS_UPDATED_EVENT, refreshBudgetFromBookings as EventListener);
      window.removeEventListener('storage', refreshBudgetFromBookings);
    };
  }, [vendors, loadAttempt]);

  useEffect(() => {
    void refreshEventsFromApi().then(setEvents).catch(() => setEvents([]));
  }, []);

  // Deep-link support: ?category=<name> (e.g. "Open Budget" from the
  // Shipping page linking straight to Logistics) selects that category once
  // it's loaded, instead of always landing on the undifferentiated list.
  // Case-insensitive since callers pass a human label, not a slug.
  useEffect(() => {
    if (categories.length === 0) return;
    const wanted = new URLSearchParams(location.search).get('category');
    if (!wanted) return;
    const match = categories.find(c => c.name.toLowerCase() === wanted.toLowerCase());
    if (match) setSelectedCategory(match.id);
  }, [categories, location.search]);

  useEffect(() => {
    void loadQuotes().then(setQuotes).catch(() => setQuotes([]));
  }, []);



  // Handle shipping import from Cost Comparison
  useEffect(() => {
    if (shippingImportData && onShippingImported) {
      // Convert into the project currency (left as-is while none is set).
      const amount = isCurrencySet(settings.currency)
        ? convertCurrency(shippingImportData.amount, shippingImportData.currency, settings.currency)
        : shippingImportData.amount;

      // Find or create "Shipping & Import" category
      let shippingCategory = categories.find(cat => cat.name === 'Shipping & Import');

      if (shippingCategory) {
        // Update existing category's planned amount
        setCategories(prev => prev.map(cat =>
          cat.name === 'Shipping & Import'
            ? { ...cat, planned_amount: cat.planned_amount + amount }
            : cat
        ));
      } else {
        // Create new category
        const newCategory: BudgetCategory = {
          id: Date.now().toString(),
          name: 'Shipping & Import',
          planned_amount: amount,
          actual_amount: 0,
          vendors: []
        };
        setCategories(prev => [...prev, newCategory]);
        setSelectedCategory(newCategory.id);
      }

      onShippingImported();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingImportData]);

  // Derive filtered categories once — used by stats, chart, table, and cards
  const filteredCategories = eventFilter
    ? categories.filter(c => {
        // Category is in-scope if it's tagged to this event at category level
        // OR if any of its individual vendor/cost items is tagged to this event.
        // Also support multi-event (event_ids[]).
        const catEventIds = c.event_ids?.length ? c.event_ids : (c.event_id ? [c.event_id] : []);
        return catEventIds.includes(eventFilter) || c.vendors.some(v => v.event_id === eventFilter);
      })
    : categories;

  const summary = calculateBudgetSummary(filteredCategories);
  usePublishRecordList('budgetCategory', filteredCategories.map(c => c.id));

  const handleSaveCategory = async (updated: BudgetCategory) => {
    const exists = categories.find(c => c.id === updated.id);
    try {
      if (!exists) {
        // New category: POST to API to get the real DB integer ID
        const { apiCreateBudgetCategory } = await import('../utils/budgetStorage');
        const created = await apiCreateBudgetCategory(updated);
        // Use the API-assigned ID so future updates go to the right path
        setCategories(prev => [...prev, created ? { ...updated, id: created.id } : updated]);
      } else {
        // Existing category: update cache and persist
        setCategories(prev => prev.map(c => c.id === updated.id ? updated : c));
        const { apiUpdateBudgetCategory } = await import('../utils/budgetStorage');
        await apiUpdateBudgetCategory(updated);
      }
      showSuccess('Budget category saved');
    } catch (err) {
      showError('Failed to save budget category. Please try again.');
      return;
    }
    setShowCategoryModal(false);
    setEditingCategory(null);
  };

  const handleOpenEditModal = (category: BudgetCategory, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingCategory(category);
    setShowCategoryModal(true);
  };

  const handleOpenAddModal = () => {
    setEditingCategory(null);
    setShowCategoryModal(true);
  };

  useEffect(() => {
    if (triggerAddCategory) { handleOpenAddModal(); onAddCategoryHandled?.(); }
  }, [triggerAddCategory]);

  // Sync external tab prop → internal state
  useEffect(() => {
    if (externalCategoryTab) setCategoryTab(externalCategoryTab);
  }, [externalCategoryTab]);

  // Reset tab when navigating to a different category
  useEffect(() => {
    if (categoryId) { setCategoryTab('vendors'); onBudgetCategoryTabChange?.('vendors'); }
  }, [categoryId]);

  // Trigger: open Add Vendor form from header button
  useEffect(() => {
    if (triggerAddBudgetVendor && categoryId) {
      const cat = categories.find(c => c.id === categoryId);
      if (cat) { setShowAddVendor(true); setSelectedCategory(cat.id); }
      onAddBudgetVendorHandled?.();
    }
  }, [triggerAddBudgetVendor]);

  // Trigger: save category to API from header Save button
  useEffect(() => {
    if (triggerSaveBudgetCategory && categoryId) {
      const cat = categories.find(c => c.id === categoryId);
      if (cat) {
        import('../utils/budgetStorage').then(({ apiUpdateBudgetCategory }) => {
          apiUpdateBudgetCategory(cat)
            .then(() => showSuccess('Budget category saved'))
            .catch(() => showError('Failed to save budget category. Please try again.'));
        });
      }
      onSaveBudgetCategoryHandled?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSaveBudgetCategory]);

  // Load contract attachments for a vendor item
  const loadVendorContracts = useCallback(async (vendorId: string) => {
    if (vendorContracts[vendorId]) return; // already loaded
    try {
      const atts = await getAttachments('budget_item', vendorId);
      const contracts = atts.filter(a => a.label === 'contract');
      setVendorContracts(prev => ({ ...prev, [vendorId]: contracts }));
    } catch { /* non-fatal */ }
  }, [vendorContracts]);

  const handleContractFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !contractUploadVendorId) return;
    setContractUploading(true);
    try {
      const att = await uploadImage(file, {
        entityType: 'budget_item',
        entityId: contractUploadVendorId,
        label: 'contract',
      });
      setVendorContracts(prev => ({ ...prev, [contractUploadVendorId]: [...(prev[contractUploadVendorId] ?? []), att] }));
      showSuccess('Contract attached');
    } catch {
      showError('Failed to upload contract');
    } finally {
      setContractUploading(false);
      setContractUploadVendorId(null);
      if (contractFileRef.current) contractFileRef.current.value = '';
    }
  }, [contractUploadVendorId, showSuccess, showError]);

  const handleRemoveContract = useCallback(async (vendorId: string, att: Attachment) => {
    try {
      await deleteAttachment(att.id);
      setVendorContracts(prev => ({ ...prev, [vendorId]: (prev[vendorId] ?? []).filter(a => a.id !== att.id) }));
    } catch {
      showError('Failed to remove contract');
    }
  }, [showError]);

  // Notify parent when vendor editing state changes (covers both edit and add-new)
  useEffect(() => {
    onVendorEditStateChange?.(editingVendorId !== null || showAddVendor);
  }, [editingVendorId, showAddVendor]);

  // Eagerly load contract attachments for all vendors in the detail view
  useEffect(() => {
    if (!categoryId) return;
    const cat = categories.find(c => c.id === categoryId);
    if (!cat) return;
    cat.vendors.forEach(v => {
      if (vendorContracts[v.vendor_id] !== undefined) return;
      getAttachments('budget_item', v.vendor_id)
        .then(atts => {
          const contracts = atts.filter(a => a.label === 'contract');
          setVendorContracts(prev => ({ ...prev, [v.vendor_id]: contracts }));
        })
        .catch(() => { /* non-fatal */ });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  // Emit counts to the breadcrumb. These count QUOTES, not budget-linked
  // vendors: a category typically has many quotes and no linked vendor until
  // one is accepted, so counting vendors showed "0 active · 0 rejected" next
  // to a full quotes table. Accepted quotes are counted too — they leave the
  // quotes list (which filters them out) but are very much still active.
  // Load this category's purchases. Filtered server-side now
  // (budget_category_id param) — the old client-side filter read
  // r.budget_category_id off each row, a field the endpoint never actually
  // returned (always undefined), so this silently matched nothing before.
  // The server-side filter is also allocation-aware (see ans-backend's
  // GET /procurement/orders), which a client-side re-filter here couldn't
  // easily replicate without duplicating that same attribution logic.
  useEffect(() => {
    if (!categoryId) { setCategoryOrders([]); return; }
    let cancelled = false;
    apiGet<{ data?: unknown[]; orders?: unknown[] }>(planningPath(`/procurement/orders?limit=200&budget_category_id=${encodeURIComponent(categoryId)}`))
      .then((res: { data?: unknown[]; orders?: unknown[] }) => {
        if (cancelled) return;
        const rows = (res.data ?? res.orders ?? []) as Record<string, unknown>[];
        setCategoryOrders(
          rows
            .map(r => ({
              id: String(r.id),
              status: String(r.status ?? 'draft'),
              marketplace: String(r.marketplace ?? ''),
              notes: (r.notes as string) ?? null,
              total_amount_cents: Number(r.total_amount_cents) || 0,
              currency: String(r.currency ?? ''),
              order_date: (r.order_date as string) ?? null,
              vendor_name: (r.vendor_name as string) ?? null,
            }))
        );
      })
      .catch(() => { if (!cancelled) setCategoryOrders([]); });
    return () => { cancelled = true; };
  }, [categoryId]);

  // Trigger: save from header — both Add and Edit now submit through the
  // same registered AddVendorForm instance (add vs. edit mode).
  useEffect(() => {
    if (triggerSaveBudgetVendor) {
      if (showAddVendor || editingVendorId) {
        addVendorSubmitRef.current?.();
      }
      onSaveBudgetVendorHandled?.();
    }
  }, [triggerSaveBudgetVendor]);

  // Trigger: cancel from header — handles both editing existing and adding new
  useEffect(() => {
    if (triggerCancelBudgetVendor) {
      setEditingVendorId(null);
      setVendorEditDraft({});
      setShowAddVendor(false);
      onCancelBudgetVendorHandled?.();
    }
  }, [triggerCancelBudgetVendor]);

  // budget-add-payment custom event (dispatched from Header toolbar)
  useEffect(() => {
    const handler = () => {
      const cat = categoryId ? categories.find(c => c.id === categoryId) : undefined;
      if (!cat?.vendors.length) return;
      setGlobalPaymentVendorId(cat.vendors[0].vendor_id);
      setShowGlobalPaymentModal(true);
    };
    window.addEventListener('budget-add-payment', handler);
    return () => window.removeEventListener('budget-add-payment', handler);
  }, [categories, categoryId]);

  // Export button (Header): each tab of the open category registers what it
  // shows, from data already held in state; Header exports the open tab.
  const exportCategory = categoryId ? categories.find(c => c.id === categoryId) : undefined;
  const exportCtx = { categoryTab };
  useExport('budget-category-vendors', {
    rows: (exportCategory?.vendors ?? []).filter(v => !v.procurement_order_id).map(v => ({
      vendor: v.vendor_name,
      service: (!v.service || v.service === 'Vendor Service') ? '' : v.service,
      cost: vendorGrossCostBase(v),
      paid: netPaid(v.payments),
      status: VENDOR_BOOKING_STATUS_LABELS[(v.booking_status || 'inquiry') as VendorBookingStatus],
    })),
    ctx: exportCtx,
  });
  useExport('budget-category-payments', {
    rows: (exportCategory?.vendors ?? []).flatMap(v => v.payments.map(p => ({
      vendor: v.vendor_name,
      date: formatDate(p.date),
      amount: p.amount,
      method: p.method,
      status: p.status,
      reference: p.reference || '',
      notes: p.notes || '',
    }))),
    ctx: exportCtx,
  });
  useExport('budget-category-summary', {
    rows: exportCategory ? [{
      category: exportCategory.name,
      planned: exportCategory.planned_amount,
      actual: exportCategory.actual_amount,
      paid: exportCategory.vendors.flatMap(v => v.payments)
        .filter(p => p.status === 'completed')
        .reduce((sum, p) => sum + p.amount, 0),
    }] : [],
    ctx: exportCtx,
  });

  // AddVendorForm's "new vendor" branch (as opposed to editing an existing
  // budget item) always creates a real Quote instead of a committed
  // budget_items row — that already-stated intent ("a newly-added vendor is
  // a quote, not a booking yet") was never actually backed by a real quote
  // record before this form was consolidated with QuotesTracker's "+ New
  // Quote" form: every "add vendor" created a committed budget_items row
  // regardless. A quote only turns into a budget line once accepted (see
  // Quotes tab) and eventually marked contract signed.
  const addVendorToCategory = async (data: {
    categoryId: string;
    vendorId?: string;
    vendorName: string;
    eventIds: string[];
    dateReceived?: string;
    notes?: string;
  }) => {
    let created: VendorQuote;
    try {
      created = await createQuote(data);
      setQuotes(prev => [created, ...prev]);
    } catch {
      showError('Failed to save vendor quote. Please try again.');
      return;
    }
    showSuccess(`"${data.vendorName}" added to Quotes — add itemized costs on its page.`);
    setShowAddVendor(false);
    navigate(`/quotes/${fmtId('QT', created.id)}`);
  };

  const handleAcceptQuote = async (quote: VendorQuote) => {
    try {
      const { quote: updated } = await acceptQuote(quote.id);
      setQuotes(prev => prev.map(q => q.id === updated.id ? updated : q));
      showSuccess(`Accepted — "${updated.vendorName}" now has a budget line.`);
      void refreshBudgetFromApi().then(setCategories);
    } catch {
      showError('Failed to accept quote.');
    }
  };

  const handleRejectQuote = async (quote: VendorQuote) => {
    try {
      const updated = await rejectQuote(quote.id);
      setQuotes(prev => prev.map(q => q.id === updated.id ? updated : q));
    } catch {
      showError('Failed to reject quote.');
    }
  };

  const handleAddVendorQuote = (data: {
    vendorId?: string;
    vendorName: string;
    categoryId: string;
    eventIds: string[];
    dateReceived?: string;
    notes?: string;
  }) => {
    void addVendorToCategory(data);
  };

  const handleEditVendorSubmit = (updates: Partial<VendorBudgetItem>) => {
    if (!editingVendorId || !categoryId) return;
    const cat = categories.find(c => c.id === categoryId);
    const vendor = cat?.vendors.find(v => v.vendor_id === editingVendorId);
    if (!cat || !vendor) return;
    // Contract Signed + Event stay outside the shared form (they're edit-only
    // concerns tied to the Attach Contract UI, which lives in this component) —
    // merge whatever was changed there in on top of the form's own fields.
    const merged = { ...updates, ...vendorEditDraft };
    updateVendorInCategory(cat.id, editingVendorId, merged);
    // Must use vendor.id (the real budget_items.id), not editingVendorId
    // (a vendor's own text slug) — apiUpdateBudgetItem silently no-ops on
    // any non-numeric id, so this previously never persisted.
    void apiUpdateBudgetItem(vendor.id, merged);
    setEditingVendorId(null);
    setVendorEditDraft({});
  };

  const addPayment = async (categoryId: string, vendorId: string, payment: Payment) => {
    // Must resolve to vendor.id (the real budget_items.id) before persisting —
    // same vendor.id vs vendor_id landmine as updateVendorInCategory above;
    // this used to only ever update local state and revert on the next refresh.
    const cat = categories.find(c => c.id === categoryId);
    const vendor = cat?.vendors.find(v => v.vendor_id === vendorId);
    if (!vendor) return;

    let saved: Payment | null = null;
    try {
      saved = await apiCreatePayment(vendor.id, payment);
    } catch {
      showError('Failed to save payment');
      return;
    }
    if (!saved) {
      showError('Failed to save payment');
      return;
    }

    setCategories(prev => prev.map(c => {
      if (c.id !== categoryId) return c;
      return {
        ...c,
        vendors: c.vendors.map(v => {
          if (v.vendor_id !== vendorId) return v;
          const newPayments = [...v.payments, saved as Payment];
          const totalPaid = newPayments
            .filter(p => p.status === 'completed')
            .reduce((sum, p) => sum + p.amount, 0);

          let payment_status = v.payment_status;
          if (totalPaid === 0) payment_status = 'not-started';
          else if (totalPaid >= (v.plannedCostBase ?? v.planned_cost)) payment_status = 'fully-paid';
          else if (totalPaid > 0) payment_status = 'partially-paid';

          return { ...v, payments: newPayments, payment_status };
        })
      };
    }));
    // The server derives the line's actual and status from its payments: take them from there.
    void refreshBudgetFromApi().then(setCategories).catch(() => {});
    showSuccess('Payment recorded');
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'fully-paid': return '#10b981';
      case 'deposit-paid': return '#3b82f6';
      case 'partially-paid': return '#f59e0b';
      case 'overdue': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // ── Category Detail View ──────────────────────────────────────────────
  const detailCategory = categoryId ? categories.find(c => c.id === categoryId) : undefined;

  const handleDeleteCategory = (catId: string) => {
    setDeleteCategoryTarget(catId);
  };

  const activeActual = (vendors: VendorBudgetItem[]) =>
    vendors.filter(v => v.booking_status !== 'rejected' && v.source !== 'contribution').reduce((sum, v) => sum + (v.actualCostBase ?? v.actual_cost ?? 0), 0);

  const updateVendorInCategory = (catId: string, vendorId: string, changes: Partial<VendorBudgetItem>) => {
    // Must resolve to vendor.id (the real budget_items.id) before persisting —
    // vendorId here is vendor_id (a text slug); apiUpdateBudgetItem silently
    // no-ops on any non-numeric id, which previously meant Reject/Restore
    // only ever changed local state and reverted on the next refresh.
    const cat = categories.find(c => c.id === catId);
    const vendor = cat?.vendors.find(v => v.vendor_id === vendorId);
    setCategories(prev => prev.map(c => {
      if (c.id !== catId) return c;
      const updatedVendors = c.vendors.map(v => v.vendor_id === vendorId ? { ...v, ...changes } : v);
      return { ...c, vendors: updatedVendors, actual_amount: activeActual(updatedVendors) };
    }));
    if (vendor) void apiUpdateBudgetItem(vendor.id, changes);
  };

  const removeVendorFromCategory = (catId: string, vendorId: string) => {
    // Same vendor.id vs vendor_id landmine as above — the category-level PUT
    // this used to call has no concept of the vendors sub-collection at all
    // (only name/planned_amount/etc.), so the delete silently never reached
    // the backend and the vendor reappeared on the next refresh.
    const cat = categories.find(c => c.id === catId);
    const vendor = cat?.vendors.find(v => v.vendor_id === vendorId);
    setCategories(prev => prev.map(c => {
      if (c.id !== catId) return c;
      const updatedVendors = c.vendors.filter(v => v.vendor_id !== vendorId);
      return { ...c, vendors: updatedVendors, actual_amount: activeActual(updatedVendors) };
    }));
    if (vendor) void apiDeleteBudgetItem(vendor.id);
  };

  if (isLoading) {
    return <div className="wt-container fade-in"><div className="planning-loading">Loading budget data…</div></div>;
  }
  if (loadError != null) {
    return <div className="wt-container fade-in"><LoadErrorBox what="the budget" error={loadError} onRetry={() => setLoadAttempt(n => n + 1)} /></div>;
  }

  if (categoryId) {
    if (!detailCategory) {
      return (
        <div className="wt-container fade-in">
            <div className="inline-form-page">
              <div className="inline-form-header">
                <button className="btn-back" onClick={() => navigate('/budget')}>
                  <span className="bt-back-arrow">&larr;</span> Back
                </button>
                <h2>Category Not Found</h2>
              </div>
              <div className="inline-form-page">
                <p>The budget category you are looking for does not exist.</p>
              </div>
            </div>
        </div>
      );
    }

    const percentUsed = detailCategory.planned_amount > 0
      ? (detailCategory.actual_amount / detailCategory.planned_amount) * 100
      : 0;
    const isOverBudget = detailCategory.actual_amount > detailCategory.planned_amount;

    // A purchase gets its own Purchase Orders section (categoryOrders,
    // sourced from procurement_orders directly) instead of a row in the
    // Vendor table below — computed once here so the table's `data` and its
    // length-gated empty state read the same filtered list.
    const bookingVendors = detailCategory.vendors.filter(v => !v.procurement_order_id);

    const allPayments = detailCategory.vendors.flatMap(v =>
      v.payments.map(p => ({ ...p, vendor_name: v.vendor_name, vendor_id: v.vendor_id }))
    );
    const totalPaidAll = allPayments.filter(p => p.status === 'completed').reduce((s, p) => s + p.amount, 0);

    const remaining = detailCategory.planned_amount - detailCategory.actual_amount;

    // Accepted quotes already have their own row in the Bookings table above
    // (accepting creates the real budget_items line) — listing them again
    // here would just duplicate the same vendor twice.
    const categoryQuotes = quotes.filter(q => q.categoryId === detailCategory.id && q.status !== 'accepted');

    return (
      <>
        {/* Hidden file input for contract uploads */}
        <input
          ref={contractFileRef}
          type="file"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          className="u-hidden"
          onChange={handleContractFileChange}
        />
        <div className="wt-container fade-in">
          <div className="detail-layout">
            <div className="detail-main">

            {/* ── Details Tab ── */}
            {categoryTab === 'overview' && (
              <div className="inline-form-page">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Category Name</label>
                      <input type="text" value={detailCategory.name} onChange={(e) => setCategories(prev => prev.map(c => c.id === categoryId ? { ...c, name: e.target.value } : c))} />
                    </div>
                    <div className="form-group">
                      <label>Planned Amount ({getCurrencySymbol()})</label>
                      <input
                        className="no-spinner"
                        type={focusedAmountField === 'planned' ? 'number' : 'text'}
                        min="0"
                        value={focusedAmountField === 'planned'
                          ? (detailCategory.planned_amount || '')
                          : (detailCategory.planned_amount > 0 ? detailCategory.planned_amount.toLocaleString(getAppLocale()) : '')}
                        onFocus={() => setFocusedAmountField('planned')}
                        onBlur={() => setFocusedAmountField(null)}
                        onChange={(e) => setCategories(prev => prev.map(c => c.id === categoryId ? { ...c, planned_amount: Number(e.target.value) || 0 } : c))}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <RichTextEditor
                    label="Notes"
                    value={detailCategory.notes || ''}
                    onChange={(val) => setCategories(prev => prev.map(c => c.id === categoryId ? { ...c, notes: val } : c))}
                    placeholder="Additional notes..."
                    rows={3}
                  />
                  <div className="form-group">
                    <label>Events</label>
                    <TagMultiSelect
                      values={(detailCategory.event_ids ?? (detailCategory.event_id ? [detailCategory.event_id] : [])).map(eid => events.find(e => e.id === eid)?.title ?? eid)}
                      options={events.map(ev => ev.title)}
                      onChange={titles => {
                        const ids = titles.map(t => events.find(ev => ev.title === t)?.id ?? t);
                        setCategories(prev => prev.map(c => c.id === categoryId ? { ...c, event_ids: ids, event_id: ids[0] } : c));
                      }}
                      placeholder="All events"
                      allowCreate={false}
                    />
                    <p className="form-hint">Tag one or more events so the event filter can scope to this category.</p>
                  </div>
              </div>
            )}

            {/* ── Vendors Tab ── */}
            {categoryTab === 'vendors' && !(showAddVendor && selectedCategory === detailCategory.id) && (() => {
              const all = quotes.filter(q => q.categoryId === String(detailCategory.id));
              return (
                <section className="stats-grid">
                  <MetricCard label="Active Quotes" value={all.filter(q => q.status !== 'rejected').length} tone="blue" icon="list" />
                  <MetricCard label="Rejected" value={all.filter(q => q.status === 'rejected').length} tone="red" icon="x" />
                </section>
              );
            })()}
            {categoryTab === 'vendors' && (
              showAddVendor && selectedCategory === detailCategory.id ? (
                <AddVendorForm
                  vendors={vendors}
                  categories={categories}
                  events={events}
                  initialCategoryId={selectedCategory ?? undefined}
                  onSubmitQuote={handleAddVendorQuote}
                  onCancel={() => setShowAddVendor(false)}
                  getCurrencySymbol={getCurrencySymbol}
                  onRegisterSubmit={(fn) => { addVendorSubmitRef.current = fn; }}
                />
              ) : editingVendorId ? (
                <AddVendorForm
                  vendors={vendors}
                  editingVendor={detailCategory.vendors.find(v => v.vendor_id === editingVendorId)}
                  onSubmit={handleEditVendorSubmit}
                  onCancel={() => { setEditingVendorId(null); setVendorEditDraft({}); }}
                  getCurrencySymbol={getCurrencySymbol}
                  onRegisterSubmit={(fn) => { addVendorSubmitRef.current = fn; }}
                />
              ) : (
              <div>
                {/* No empty state when there are no linked vendors: the Quotes
                    section directly below already shows the quotes and the way
                    to add one, so this only repeated the same prompt above a
                    populated table. */}
                {bookingVendors.length > 0 && (
                  <DataTable
                    // A purchase gets its own Purchase Orders section below
                    // instead of a row here — it used to be filtered from
                    // `data` with nowhere else to go, which made a category
                    // whose only commitment was a purchase show "No data
                    // available" here despite the sidebar's count still
                    // including it. bookingVendors is the same filter,
                    // computed once above so the count and the table can't
                    // drift from each other the way they did before.
                    data={bookingVendors}
                    rowClassName={vendor => vendor.booking_status === 'rejected' ? 'dt-row-rejected' : ''}
                    onRowClick={vendor => {
                      // Purchase orders and vendor bookings have no page any
                      // more (their API routes are disabled and the screens
                      // were removed), so those rows are not clickable.
                      if (vendor.procurement_order_id || vendor.source === 'vendor-booking') return;
                      setSelectedCategory(detailCategory.id);
                      setEditingVendorId(vendor.vendor_id);
                    }}
                    columns={[
                      {
                        key: 'vendor',
                        header: 'Vendor',
                        render: (_v, vendor) => <strong className={vendor.booking_status === 'rejected' ? 'dt-cell-strikethrough' : ''}>{vendor.vendor_name}</strong>,
                      },
                      {
                        key: 'service',
                        header: 'For',
                        // "Vendor Service" is the placeholder the importer wrote
                        // when it had nothing better; showing it is worse than
                        // showing nothing, because it looks like real detail.
                        render: (_v, vendor) =>
                          !vendor.service || vendor.service === 'Vendor Service' ? '—' : vendor.service,
                      },
                      {
                        key: 'cost',
                        header: 'Cost',
                        render: (_v, vendor) => {
                          const gross = vendorGrossCostBase(vendor);
                          const revenue = vendor.lineRevenue ?? vendor.asoebiGuestRevenue;
                          if (vendor.fxRateMissing) {
                            return (
                              <span>
                                {formatCurrency(vendor.planned_cost, vendor.currency)}{' '}
                                <UnconvertedMark currency={vendor.currency} />
                              </span>
                            );
                          }
                          if (revenue == null) return formatCurrency(gross);
                          // Revenue earned back against this line offsets its
                          // gross spend — show both so the money coming in
                          // isn't hidden inside a single number.
                          const revenueLabel = vendor.lineRevenueLabel ?? 'guest resale';
                          // netActualCost from the API is gross (native
                          // currency) minus revenue (assumed NGN, e.g. guest
                          // resale) — re-derive it from the NGN-converted
                          // gross above rather than trust that raw figure,
                          // so a non-NGN line with revenue still nets right.
                          const netBase = gross - revenue;
                          return (
                            <span>
                              {formatCurrency(netBase)}
                              <span className="form-hint bt-hint-block">
                                {formatCurrency(gross)} spent − {formatCurrency(revenue)} {revenueLabel}
                              </span>
                            </span>
                          );
                        },
                      },
                      { key: 'paid', header: 'Paid', render: (_v, vendor) => formatCurrency(netPaid(vendor.payments)) },
                      {
                        key: 'remaining',
                        header: 'Remaining',
                        render: (_v, vendor) => {
                          const effectiveCost = vendorGrossCostBase(vendor);
                          const remaining = effectiveCost - netPaid(vendor.payments);
                          return <span style={{ /* ds-dynamic: color flips negative-remaining red */ color: remaining < 0 ? 'var(--danger)' : 'inherit' }}>{formatCurrency(remaining)}</span>;
                        },
                      },
                      {
                        key: 'status',
                        header: 'Status',
                        // Reflects the vendor's real booking_status (Inquiry,
                        // Quoted, Negotiating, Booked, Contract Signed, Paid
                        // in Full, ...) instead of collapsing everything
                        // that isn't contract_signed into a single "Pending"
                        // — that made a confirmed booking indistinguishable
                        // from a cold inquiry nobody's replied to yet.
                        render: (_v, vendor) => {
                          // A purchase has no booking status. It was showing
                          // "Inquiry" — the default — which reads as an
                          // unanswered vendor enquiry for something that was
                          // bought and paid for. Say what it actually is.
                          if (vendor.procurement_order_id) {
                            return <span className="ds-badge ds-badge-info">Purchase</span>;
                          }
                          const status = (vendor.booking_status || 'inquiry') as VendorBookingStatus;
                          return (
                            <span
                              className="ds-badge ds-badge-dynamic"
                              style={{ /* ds-dynamic: badge accent color from vendor booking status */ '--ds-badge-accent': VENDOR_BOOKING_STATUS_COLORS[status] } as React.CSSProperties}
                            >
                              {VENDOR_BOOKING_STATUS_LABELS[status]}
                            </span>
                          );
                        },
                      },
                    ]}
                  />
                )}

                <div className="bt-quotes-section bt-mt-6">
                  <p className="form-section-title">Quotes</p>
                  <DataTable
                    data={categoryQuotes}
                    emptyMessage="No quotes yet — bids from vendors show up here before they're accepted into a booking."
                    rowClassName={q => q.status === 'rejected' ? 'dt-row-rejected' : ''}
                    onRowClick={q => navigate(`/quotes/${fmtId('QT', q.id)}`)}
                    columns={[
                      {
                        key: 'vendor',
                        header: 'Vendor',
                        render: (_v, q) => <strong className={q.status === 'rejected' ? 'dt-cell-strikethrough' : ''}>{q.vendorName}</strong>,
                      },
                      // Same column order as the Bookings table above —
                      // Vendor, what it's for, the money, then Status. The two
                      // used to disagree (Status second here, last there), so
                      // reading down the page meant re-anchoring at each table.
                      { key: 'notes', header: 'For', render: (_v, q) => q.notes || '—' },
                      {
                        key: 'total',
                        header: 'Amount',
                        align: 'right',
                        render: (_v, q) => q.totalAmount > 0 ? formatCurrency(q.totalAmount) : '—',
                      },
                      { key: 'dateReceived', header: 'Received', render: (_v, q) => q.dateReceived || '—' },
                      {
                        key: 'status',
                        header: 'Status',
                        render: (_v, q) => (
                          <span className={`ds-badge ${q.status === 'rejected' ? 'ds-badge-danger' : 'ds-badge-neutral'}`}>
                            {q.status.charAt(0).toUpperCase() + q.status.slice(1)}
                          </span>
                        ),
                      },
                      actionsColumn<VendorQuote>([
                        {
                          key: 'accept', icon: 'check', title: 'Accept', variant: 'success',
                          show: q => q.status === 'pending',
                          onClick: handleAcceptQuote,
                        },
                        {
                          key: 'reject', icon: 'x', title: 'Reject', variant: 'danger',
                          show: q => q.status === 'pending',
                          onClick: handleRejectQuote,
                        },
                      ]),
                    ]}
                  />
                </div>

                {/* Purchases are their own record type, not a vendor
                    booking with a status stretched to fit — same table (and
                    the same columns, PURCHASE_ORDER_COLUMNS) as the full
                    Purchases tab, shown here too since this Overview tab is
                    where Vendor and Quotes already live and a category with
                    a real procurement order shouldn't need a second tab
                    click to see it. Hidden entirely when there are none,
                    same as Quotes has no such gate but this one does — a
                    category with real purchases is the less common case. */}
                {categoryOrders.length > 0 && (
                  <div className="bt-quotes-section bt-mt-6">
                    <p className="form-section-title">Purchase Orders</p>
                    <DataTable
                      data={categoryOrders}
                      hoverable
                      compact
                      columns={PURCHASE_ORDER_COLUMNS}
                    />
                  </div>
                )}
              </div>
              )
            )}

            {/* ── Payments Tab ── */}
            {categoryTab === 'purchases' && (
              <div className="bt-tab-panel">
                {/* Purchases are their own record type with their own
                    lifecycle (draft → ordered → shipped → delivered) and their
                    own columns. Sharing the bookings table forced them to
                    borrow a booking status, which is why a paid-for robe
                    displayed as "Inquiry". */}
                <DataTable
                  data={categoryOrders}
                  hoverable
                  compact
                  emptyMessage="No purchases linked to this category yet."
                  columns={PURCHASE_ORDER_COLUMNS}
                />
              </div>
            )}

            {categoryTab === 'payments' && (
              <div className="bt-payments-flat-wrap">
                <DataTable
                  data={allPayments}
                  hoverable
                  emptyMessage='No payments recorded. Use "Add Payment" in the toolbar to record the first payment.'
                  onRowClick={p => navigate(`/payments/record/${fmtId('PY', p.id)}`, { state: { editPayment: p, vendorId: p.vendor_id } })}
                  columns={[
                    { key: 'vendor', header: 'Vendor', className: 'bt-col-vendor', render: (_v, p) => <strong>{p.vendor_name}</strong> },
                    // formatDate handles a missing/unparseable date; new Date(...)
                    // the old direct date call rendered the literal "Invalid Date".
                    { key: 'date', header: 'Date', render: (_v, p) => formatDate(p.date) },
                    { key: 'amount', header: 'Amount', render: (_v, p) => formatCurrency(p.amount) },
                    { key: 'method', header: 'Method', render: (_v, p) => <span className="bt-capitalize">{p.method.replace(/-/g, ' ')}</span> },
                    { key: 'status', header: 'Status', render: (_v, p) => <span className={`ds-badge ds-badge-${PAYMENT_DS[p.status] ?? 'neutral'}`}>{p.status}</span> },
                    {
                      key: 'reference',
                      header: 'Reference',
                      render: (_v, p) => p.reference ? <span className="bt-cell-clamp"><MaskedValue value={p.reference} /></span> : <span className="bt-cell-clamp">—</span>,
                    },
                    {
                      key: 'paid_by',
                      header: 'Paid by',
                      render: (_v, p) => <span>{p.paid_by || '—'}</span>,
                    },
                    {
                      key: 'notes',
                      header: 'Notes',
                      className: 'bt-col-notes',
                      clamp: 4,
                      render: (_v, p) => <span title={p.notes || undefined}>{p.notes || '—'}</span>,
                    },
                  ]}
                />
              </div>
            )}

            </div>{/* end bt-detail-main */}

            {/* ── Right Sidebar ── */}
            <aside className="detail-sidebar">
              {categoryTab !== 'overview' && (
                <section className="side-section">
                  <h4>Details</h4>
                  <div className="stat-row">
                    <span>Category</span>
                    <strong>{detailCategory.name}</strong>
                  </div>
                  <div className="stat-row">
                    <span>Events</span>
                    <strong>
                      {(detailCategory.event_ids?.length
                        ? detailCategory.event_ids
                        : detailCategory.event_id ? [detailCategory.event_id] : []
                      ).map(eid => events.find(e => e.id === eid)?.title ?? eid).join(', ') || 'All events'}
                    </strong>
                  </div>
                  {detailCategory.notes && (
                    <p className="form-hint bt-mt-2">{detailCategory.notes}</p>
                  )}
                </section>
              )}
              <section className="side-section">
                <h4>Budget Summary</h4>
                <div className="stat-row">
                  <span>Planned</span>
                  <strong>{formatCurrency(detailCategory.planned_amount)}</strong>
                </div>
                <div className="stat-row">
                  <span>Actual Cost</span>
                  <strong>{formatCurrency(detailCategory.actual_amount)}</strong>
                </div>
                <UnconvertedNote count={detailCategory.unconvertedCount ?? 0} />
                {!!detailCategory.contributed_amount && (
                  <div className="stat-row">
                    <span>Funded (gifts)</span>
                    <strong className="bt-text-success">
                      {formatCurrency(detailCategory.contributed_amount)}
                    </strong>
                  </div>
                )}
                <div className="stat-row">
                  <span>Total Paid</span>
                  <strong className="bt-text-success">{formatCurrency(totalPaidAll)}</strong>
                </div>
                <div className="bt-sidebar-divider" />
                <div className="stat-row">
                  <span>Remaining</span>
                  <strong style={{ /* ds-dynamic: color flips negative-remaining red */ color: remaining < 0 ? 'var(--danger, #c62828)' : 'var(--text-dark)' }}>
                    {formatCurrency(remaining)}
                  </strong>
                </div>
                <div className="bt-sidebar-progress-track">
                  <div
                    className="bt-sidebar-progress-fill"
                    style={{ /* ds-dynamic: progress bar width/color from computed budget usage */
                      width: `${Math.min(100, percentUsed)}%`,
                      background: isOverBudget ? 'var(--danger, #c62828)' : 'var(--primary, #c19660)',
                    }}
                  />
                </div>
                <div className="bt-sidebar-percent">{percentUsed.toFixed(1)}% of budget used</div>
              </section>

              {/* Was "Linked vendors / With payments" — two counts that were
                  both 4, because they only differ when a line item has no
                  payment recorded. That is a data-completeness fact, not
                  something you make a decision on. These are the numbers you
                  actually act on: what is still owed, and what is still
                  undecided. */}
              {(() => {
                const outstanding = Math.max(0, detailCategory.actual_amount - totalPaidAll);
                const pending = quotes.filter(q => q.categoryId === String(detailCategory.id) && q.status === 'pending');
                const pendingValue = pending.reduce((sum, q) => sum + (q.totalAmount || 0), 0);
                return (
                  <section className="side-section">
                    <h4>Commitments</h4>
                    <div className="stat-row">
                      {/* Not "Line items" — a vendor booking or a PO can
                          each have their own line items internally, so that
                          label read as counting inside one record rather
                          than how many separate commitments (bookings +
                          purchases) exist in this category. */}
                      <span>Committed items</span>
                      <strong>{detailCategory.vendors.length}</strong>
                    </div>
                    <div className="stat-row">
                      <span>Still to pay</span>
                      <strong className={outstanding > 0 ? 'bt-stat-due' : undefined}>
                        {formatCurrency(outstanding)}
                      </strong>
                    </div>
                    <div className="stat-row">
                      <span>Quotes awaiting a decision</span>
                      <strong>{pending.length}</strong>
                    </div>
                    {pending.length > 0 && (
                      <div className="stat-row">
                        <span>If all accepted</span>
                        <strong>{formatCurrency(pendingValue)}</strong>
                      </div>
                    )}
                  </section>
                );
              })()}
            </aside>

          </div>
        </div>
        {showGlobalPaymentModal && (
          <Modal open={showGlobalPaymentModal} onClose={() => setShowGlobalPaymentModal(false)} title="Add Payment" size="small">
            <div className="form-group bt-form-group-tight">
              <label className="bt-vendor-label">Vendor</label>
              <SearchableSelect
                value={globalPaymentVendorId}
                onChange={setGlobalPaymentVendorId}
                options={detailCategory.vendors.map(v => ({ value: v.vendor_id, label: v.vendor_name }))}
              />
            </div>
            <AddPaymentForm
              onSubmit={async (payment) => {
                await addPayment(detailCategory.id, globalPaymentVendorId, payment);
                setShowGlobalPaymentModal(false);
              }}
              onCancel={() => setShowGlobalPaymentModal(false)}
              maxAmount={(() => {
                const v = detailCategory.vendors.find(vv => vv.vendor_id === globalPaymentVendorId);
                if (!v) return 0;
                const paid = netPaid(v.payments);
                return (v.plannedCostBase ?? v.planned_cost) - paid;
              })()}
              getCurrencySymbol={getCurrencySymbol}
              hideTitle
            />
          </Modal>
        )}
        <CategoryModal
          isOpen={showCategoryModal}
          onClose={() => { setShowCategoryModal(false); setEditingCategory(null); }}
          category={editingCategory}
          events={events}
          onSave={handleSaveCategory}
          getCurrencySymbol={getCurrencySymbol}
        />
      </>
    );
  }

  // ── Budget Overview (no categoryId) ──────────────────────────────────
  return (
    <>
      <div className="wt-container fade-in">

      {/* Stats Grid - always visible */}
      <section className="stats-grid">
        <MetricCard label="Planned Budget" value={formatCurrencyCompact(summary.totalPlanned)} tone="pink" icon="wallet" />
        <MetricCard label="Actual Cost" value={formatCurrencyCompact(summary.totalActual)} tone="blue" icon="check" />
        <MetricCard label="Total Paid" value={formatCurrencyCompact(summary.totalPaid)} tone="green" icon="dollar" />
        <MetricCard label="Remaining" value={formatCurrencyCompact(summary.totalRemaining)} tone="teal" icon="wallet" />
        <ProgressMetricCard label="Budget Used" value={`${summary.percentageSpent.toFixed(1)}%`} progress={summary.percentageSpent} tone="orange" icon="chart" />
      </section>

      {/* Cost per head - fully-loaded average vs marginal, always visible */}
      <CostPerHeadPanel formatCurrency={formatCurrency} />

      {/* Chart - cards view only */}
      {viewMode === 'cards' && (
        <BudgetChart categories={filteredCategories} formatCurrency={formatCurrency} />
      )}

      {/* Budget Table View */}
      {viewMode === 'table' && (
        <section className="budget-table-section">
        <DataTable
          data={filteredCategories}
          columns={[
            {
              key: 'name',
              header: 'Category',
              sortable: true,
              render: (value) => (
                <div className="category-name-cell">
                  <strong>{value}</strong>
                </div>
              )
            },
            {
              key: 'planned_amount',
              header: 'Planned Budget',
              sortable: true,
              align: 'right',
              render: (value) => formatCurrency(value || 0)
            },
            {
              key: 'actual_amount',
              header: 'Actual Cost',
              sortable: true,
              align: 'right',
              render: (value) => formatCurrency(value || 0)
            },
            {
              key: 'remaining',
              header: 'Remaining',
              sortable: true,
              align: 'right',
              render: (value, row) => {
                const planned = row.planned_amount || 0;
                const actual = row.actual_amount || 0;
                const remaining = planned - actual;
                const isOverBudget = remaining < 0;
                return (
                  <span className={isOverBudget ? 'over-budget' : 'under-budget'}>
                    {formatCurrency(remaining)}
                  </span>
                );
              }
            },
            {
              key: 'percentage',
              header: '% Used',
              sortable: true,
              align: 'right',
              render: (value, row) => {
                const planned = row.planned_amount || 0;
                const actual = row.actual_amount || 0;
                const percentage = planned > 0 ? (actual / planned) * 100 : 0;
                const isOverBudget = percentage > 100;
                return (
                  <div className="percentage-cell">
                    <span className={`percentage-value ${isOverBudget ? 'over-budget' : ''}`}>
                      {percentage.toFixed(1)}%
                    </span>
                    <div className="mini-progress-bar">
                      <div
                        className={`mini-progress-fill ${isOverBudget ? 'over-budget' : ''}`}
                        /* dynamic - exempt from guardrail: progress bar width is a runtime-computed percentage */
                        // eslint-disable-next-line react/forbid-component-props
                        style={ { width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              }
            },
            {
              // `vendors` holds the category's budget LINE ITEMS, not vendors —
              // the header said "Vendors" and the number never matched how many
              // vendors were actually involved.
              key: 'vendors',
              header: 'Line Items',
              sortable: false,
              render: (value) => (
                <span className="vendors-count">
                  {value?.length || 0}
                </span>
              )
            },
            {
              key: 'quotes',
              header: 'Quotes',
              sortable: false,
              render: (_value, row) => (
                <span className="vendors-count">
                  {quotes.filter(q => q.categoryId === String(row.id)).length}
                </span>
              )
            },
            {
              key: 'bookings',
              header: 'Bookings',
              sortable: false,
              render: (_value, row) => {
                const allBookings = loadBookings();
                return (
                  <span className="vendors-count">
                    {(row.vendors || []).filter((v: { vendor_id?: string }) => v.vendor_id && allBookings[v.vendor_id]).length}
                  </span>
                );
              }
            }
          ]}
          emptyMessage="No budget categories found. Add your first category to get started."
          hoverable={true}
          onRowClick={(row) => navigate(`/budget/category/${fmtId('BC', row.id)}`)}
          rowClassName={() => ''}
        />
      </section>
      )}

      {/* Budget Categories - only in cards view */}
      {viewMode === 'cards' && <div className="budget-categories">
        {filteredCategories.length === 0 && eventFilter ? (
          <EmptyState
            title="No budget categories are assigned to this event yet"
            description="Open a category and set its event to link it here."
          />
        ) : filteredCategories.map(category => {
          const percentUsed = category.planned_amount > 0
            ? (category.actual_amount / category.planned_amount) * 100
            : 0;
          const isOverBudget = category.actual_amount > category.planned_amount;

          return (
            <div
              key={category.id}
              className="category-card bt-cursor-pointer"
              onClick={() => navigate(`/budget/category/${fmtId('BC', category.id)}`)}
            >
              <div className="category-header">
                <div className="category-info">
                  {/* Name and its counts read as one unit — they used to sit on
                      separate lines, which read as two unrelated facts about
                      the category rather than "Service, which has these". */}
                  <div className="bt-category-title-row">
                    <h3>{category.name}</h3>
                    {/* The count used to render as a bare number with no label,
                        which read as an ID. Spell out what is being counted,
                        and show quotes and bookings alongside it. */}
                    {(() => {
                      const catQuotes = quotes.filter(q => q.categoryId === String(category.id));
                      const allBookings = loadBookings();
                      const bookingCount = category.vendors.filter(
                        v => v.vendor_id && allBookings[v.vendor_id]
                      ).length;
                      return (
                        <span className="bt-category-counts">
                          {category.vendors.length} {category.vendors.length === 1 ? 'item' : 'items'}
                          {' · '}{catQuotes.length} {catQuotes.length === 1 ? 'quote' : 'quotes'}
                          {' · '}{bookingCount} {bookingCount === 1 ? 'booking' : 'bookings'}
                        </span>
                      );
                    })()}
                  </div>
                  {/* Priority and event tags used to sit on two separate rows
                      (events forced onto their own full-width row so a
                      five-event span wouldn't wrap mid-line beside the count/
                      priority badges). Sharing one flex-wrap row instead: the
                      badges wrap together as a set now rather than being
                      forced apart, and short combinations stay on one line. */}
                  {(() => {
                    const eventIds = category.event_ids?.length
                      ? category.event_ids
                      : category.event_id ? [category.event_id] : [];
                    if (!category.priority && !eventIds.length) return null;
                    return (
                      <div className="bt-category-info-row">
                        {category.priority && (
                          <StatusBadge variant={PRIORITY_VARIANT[category.priority] ?? 'neutral'}>
                            {PRIORITY_LABEL[category.priority] ?? category.priority}
                          </StatusBadge>
                        )}
                        {eventIds.map(eid => (
                          <StatusBadge key={eid} variant="success">
                            {events.find(e => e.id === eid)?.title || 'Event'}
                          </StatusBadge>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                {/* RAG at a glance, top-right, before you've even read the
                    figures below — green under 80% used, amber closing in,
                    red over. */}
                {category.planned_amount > 0 && (
                  <span className={`bt-category-rag bt-category-rag--${ragLevel(percentUsed)}`}>
                    {percentUsed.toFixed(0)}%
                  </span>
                )}
              </div>
              {/* Pinned to the bottom of the card (margin-top: auto) so every
                  card's amount/progress sits at the same place regardless of
                  how much varies above it — name length, item counts, event
                  tag count. */}
              <div className="category-budget">
                {/* The spend is the figure you scan for, so it leads at full
                    size with the plan subordinate beneath it. Previously both
                    sat at the same weight behind "Planned:"/"Actual:" labels,
                    so neither stood out and the label ate the width. The
                    percentage used to repeat again here too, until the RAG
                    badge above took over as its one home. */}
                <div className="bt-amount-block">
                  <span className={`bt-amount-hero ${isOverBudget ? 'over-budget' : ''}`}>
                    {formatCurrency(category.actual_amount)}
                  </span>{' '}
                  <span className="bt-amount-sub">
                    of {formatCurrency(category.planned_amount)} planned
                  </span>
                </div>
                <div className="category-progress" style={{ /* ds-dynamic: visibility hidden until planned amount is set */ visibility: category.planned_amount > 0 ? 'visible' : 'hidden' }}>
                  <div
                    className="category-progress-fill"
                    /* dynamic - exempt from guardrail: progress bar width is a runtime-computed percentage */
                    // eslint-disable-next-line react/forbid-component-props
                    style={ {
                      width: `${Math.min(percentUsed, 100)}%`,
                      backgroundColor: isOverBudget ? '#ef4444' : '#10b981'
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>}
      </div>

    <CategoryModal
      isOpen={showCategoryModal}
      onClose={() => { setShowCategoryModal(false); setEditingCategory(null); }}
      category={editingCategory}
      events={events}
      onSave={handleSaveCategory}
      getCurrencySymbol={getCurrencySymbol}
    />
    <ConfirmDialog
      open={deleteCategoryTarget !== null}
      onClose={() => setDeleteCategoryTarget(null)}
      onConfirm={() => {
        const catId = deleteCategoryTarget!;
        setCategories(prev => prev.filter(c => c.id !== catId));
        navigate('/budget');
        import('../utils/budgetStorage').then(({ apiDeleteBudgetCategory }) => {
          void apiDeleteBudgetCategory(catId);
        });
        setDeleteCategoryTarget(null);
      }}
      title={`Delete "${categories.find(c => c.id === deleteCategoryTarget)?.name || 'this category'}"?`}
      description="This cannot be undone."
    />
    </>
  );
};

interface AddVendorFormProps {
  vendors: ParsedVendor[];
  onCancel: () => void;
  getCurrencySymbol: () => string;
  onRegisterSubmit?: (fn: () => void) => void;
  // When set, the form edits this existing budget item instead of creating
  // a new quote — same "editingVendor" branch as before, unaffected by the
  // quote-creation consolidation below.
  editingVendor?: VendorBudgetItem;
  onSubmit?: (item: Omit<VendorBudgetItem, 'id' | 'payments'>) => void;
  // The "new vendor" branch (editingVendor unset) now creates a real Quote
  // via the same field set QuotesTracker's "+ New Quote" form uses (see
  // QuoteFormFields) — categories/events and this callback are only needed
  // for that branch.
  categories?: BudgetCategory[];
  events?: StoredEvent[];
  initialCategoryId?: string;
  onSubmitQuote?: (data: {
    vendorId?: string;
    vendorName: string;
    categoryId: string;
    eventIds: string[];
    dateReceived?: string;
    notes?: string;
  }) => void;
}

const AddVendorForm: React.FC<AddVendorFormProps> = ({
  vendors, onSubmit, onCancel, getCurrencySymbol, onRegisterSubmit, editingVendor,
  categories = [], events = [], initialCategoryId, onSubmitQuote,
}) => {
  const { formatCurrency } = useSettings();
  const [avErrors, setAvErrors] = useState<Record<string, string>>({});

  // ── "New vendor" branch: creates a Quote, same field set as
  // QuotesTracker's "+ New Quote" form (via QuoteFormFields). ──
  const [quoteValues, setQuoteValues] = useState<QuoteFormValues>({
    vendorSearch: '',
    selectedVendorId: '',
    categoryId: initialCategoryId ?? '',
    eventIds: [],
    dateReceived: '',
    notes: '',
  });

  const handleQuoteChange = (updates: Partial<QuoteFormValues>) => {
    setQuoteValues(prev => ({ ...prev, ...updates }));
    if (updates.vendorSearch !== undefined && avErrors.vendor) setAvErrors(prev => ({ ...prev, vendor: '' }));
    if (updates.categoryId !== undefined && avErrors.category) setAvErrors(prev => ({ ...prev, category: '' }));
  };

  // ── "Edit existing budget item" branch: unchanged from before. ──
  // != null, not ||: a real ₦0 actual_cost (e.g. a comped invoice) must not
  // fall through to planned_cost — that silently overwrote the real ₦0 with
  // the planned amount the moment the form was opened and saved again.
  const editingCost = editingVendor
    ? (editingVendor.planned_cost ?? (editingVendor.quantity && editingVendor.unit_cost ? editingVendor.quantity * editingVendor.unit_cost : 0))
    : 0;
  const [formData, setFormData] = useState({
    service: editingVendor?.service ?? '',
    quantity: (editingVendor?.quantity ?? '') as string | number,
    unitCost: (editingVendor?.unit_cost ?? '') as string | number,
    plannedCost: editingCost,
    notes: editingVendor?.notes ?? '',
    depositAmount: (editingVendor?.depositAmount ?? '') as string | number,
    depositDueDate: editingVendor?.depositDueDate ?? '',
    balanceDueDate: editingVendor?.balanceDueDate ?? '',
    fundedByContributorId: editingVendor?.fundedByContributorId ?? '',
  });
  // Loaded lazily since most sessions never open this form — a paper trail
  // for "who's paying for this / who chose this vendor" when a family
  // disagreement comes up, not an enforcement mechanism.
  const [contributors, setContributors] = useState<Contributor[]>([]);
  useEffect(() => {
    if (editingVendor) loadContributors().then(setContributors).catch(() => {});
  }, [editingVendor]);

  // When qty or unitCost changes, auto-calculate the quoted (planned) cost.
  // Actual cost and contract-signed only get set later, once a quote turns
  // into a real booking — not at the moment a vendor is added.
  const handleQtyOrUnitChange = (field: 'quantity' | 'unitCost', raw: string) => {
    const num = Number(raw.replace(/[^\d.-]/g, '')) || 0;
    const updated = { ...formData, [field]: raw === '' ? '' : num };
    const qty = field === 'quantity' ? num : Number(formData.quantity) || 0;
    const unit = field === 'unitCost' ? num : Number(formData.unitCost) || 0;
    if (qty > 0 && unit > 0) {
      updated.plannedCost = qty * unit;
    }
    setFormData(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingVendor) {
      // New vendor → new quote, mirrors QuotesTracker's submitNewQuote.
      const errs: Record<string, string> = {};
      if (!quoteValues.vendorSearch.trim()) errs.vendor = 'Vendor name is required.';
      if (!quoteValues.categoryId) errs.category = 'Pick a budget category.';
      if (Object.keys(errs).length > 0) { setAvErrors(errs); return; }
      setAvErrors({});
      onSubmitQuote?.({
        vendorId: quoteValues.selectedVendorId || undefined,
        vendorName: quoteValues.vendorSearch.trim(),
        categoryId: quoteValues.categoryId,
        eventIds: quoteValues.eventIds,
        dateReceived: quoteValues.dateReceived || undefined,
        notes: quoteValues.notes.trim() || undefined,
      });
      return;
    }

    const errs: Record<string, string> = {};
    if (!formData.service.trim()) errs.service = 'Required';
    // Cost can't drop below what's already been paid — that's an impossible
    // state (you can't have paid more than the confirmed cost) and exactly
    // the kind of planned/actual/payments drift this app has been chasing.
    const alreadyPaid = netPaid(editingVendor.payments);
    if (alreadyPaid > 0 && formData.plannedCost < alreadyPaid) {
      errs.cost = `Can't be less than the ${formatCurrency(alreadyPaid)} already paid against this vendor.`;
    }
    if (Object.keys(errs).length > 0) { setAvErrors(errs); return; }
    setAvErrors({});
    const qty = Number(formData.quantity) || undefined;
    const unitC = Number(formData.unitCost) || undefined;
    onSubmit?.({
      vendor_id: editingVendor.vendor_id,
      vendor_name: editingVendor.vendor_name,
      service: formData.service,
      quantity: qty ?? null,
      unit_cost: unitC ?? null,
      // Editing writes the item's already-established cost field, not the
      // initial quote estimate — matches the pre-existing inline-edit
      // behavior this form replaces.
      planned_cost: formData.plannedCost,
      notes: formData.notes,
      depositAmount: Number(formData.depositAmount) || null,
      depositDueDate: formData.depositDueDate || null,
      balanceDueDate: formData.balanceDueDate || null,
      fundedByContributorId: formData.fundedByContributorId || null,
    } as any);
  };

  const qtyNum = Number(formData.quantity) || 0;
  const unitNum = Number(formData.unitCost) || 0;
  const autoTotal = qtyNum > 0 && unitNum > 0 ? qtyNum * unitNum : null;

  React.useEffect(() => {
    onRegisterSubmit?.(() => { handleSubmit({ preventDefault: () => {} } as React.FormEvent); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form className="inline-form-body bt-form-flex-col" onSubmit={handleSubmit}>

      {/* This line item is really a procurement order that predates the
          procurement module. Its vendor/deposit/contract fields do not apply to
          a marketplace purchase, and editing them here would diverge from the
          order. Send the user to the real record instead. */}
      {editingVendor && (editingVendor as { procurement_order_id?: string | null }).procurement_order_id ? (
        <div className="bt-po-redirect">
          <p className="bt-po-redirect-title">This is a procurement order</p>
          <p className="bt-po-redirect-body">
            “{editingVendor.service || editingVendor.vendor_name}” is tracked as a
            purchase order, where its supplier, shipping and landed cost live.
            This older form cannot represent those, and the procurement
            screens have been removed.
          </p>
        </div>
      ) : editingVendor ? (
        <>
          {/* Vendor picker is locked once editing — you don't change WHICH
              vendor a budget item refers to, only its details. */}
          <div className="form-group">
            <label>Vendor</label>
            <div className="bt-vendor-search-box">
              <input type="text" value={editingVendor.vendor_name} disabled className="bt-input-disabled" />
            </div>
          </div>

          <FormInput
            label="Service / Description"
            type="text"
            value={formData.service}
            onChange={(e) => { setFormData({ ...formData, service: e.target.value }); if (avErrors.service) setAvErrors(prev => ({ ...prev, service: '' })); }}
            placeholder="e.g. Couple Photography"
            error={avErrors.service}
          />

          <div className="form-row">
            <div className="form-group">
              <label>Quantity</label>
              <input
                type="number" min="1"
                value={formData.quantity}
                onChange={(e) => handleQtyOrUnitChange('quantity', e.target.value)}
                placeholder="e.g. 450"
              />
            </div>
            <div className="form-group">
              <label>Unit Cost ({getCurrencySymbol()})</label>
              <input
                type="text"
                value={formData.unitCost === '' ? '' : Number(formData.unitCost).toLocaleString(getAppLocale())}
                onChange={(e) => handleQtyOrUnitChange('unitCost', e.target.value)}
                placeholder="e.g. 70,000"
              />
            </div>
          </div>
          {autoTotal !== null && (
            <p className="bt-auto-total">= {formatCurrency(autoTotal)} total (auto-calculated)</p>
          )}

          <div className="form-group">
            <label>Cost ({getCurrencySymbol()})</label>
            <div className="currency-input-wrapper">
              <span className="currency-symbol">{getCurrencySymbol()}</span>
              <input
                type="text"
                disabled={editingVendor?.source === 'vendor-booking'}
                value={formData.plannedCost ? formData.plannedCost.toLocaleString(getAppLocale()) : ''}
                onChange={(e) => setFormData({ ...formData, plannedCost: Number(e.target.value.replace(/[^\d.-]/g, '')) || 0 })}
                placeholder="0"
              />
            </div>
            {avErrors.cost && <span className="field-error">{avErrors.cost}</span>}
            {editingVendor?.source === 'vendor-booking' && (
              <span className="form-helper">Synced from the vendor booking's line items — edit them there, not here.</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Deposit Amount ({getCurrencySymbol()}) <span className="form-label-hint">(optional)</span></label>
              <input
                type="text"
                value={formData.depositAmount === '' ? '' : Number(formData.depositAmount).toLocaleString(getAppLocale())}
                onChange={(e) => setFormData({ ...formData, depositAmount: Number(e.target.value.replace(/[^\d.-]/g, '')) || '' })}
                placeholder="e.g. 750,000"
              />
              <span className="form-helper">The vendor's required deposit per contract — lets the Payments view show "Deposit Paid" instead of just "Partially Paid."</span>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Deposit Due <span className="form-label-hint">(optional)</span></label>
              <input
                type="date"
                disabled={editingVendor?.source === 'vendor-booking'}
                value={formData.depositDueDate}
                onChange={(e) => setFormData({ ...formData, depositDueDate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Balance Due <span className="form-label-hint">(optional)</span></label>
              <input
                type="date"
                disabled={editingVendor?.source === 'vendor-booking'}
                value={formData.balanceDueDate}
                onChange={(e) => setFormData({ ...formData, balanceDueDate: e.target.value })}
              />
            </div>
          </div>
          {editingVendor?.source === 'vendor-booking' && (
            <span className="form-helper">Deposit/Balance due dates are synced from the vendor booking — edit them there, not here.</span>
          )}

          <div className="form-group">
            <label className="form-label">Funded By</label>
            <SearchableSelect
              value={formData.fundedByContributorId}
              onChange={(id) => setFormData({ ...formData, fundedByContributorId: id })}
              options={contributors.map(c => ({ value: c.id, label: c.name }))}
              placeholder="No named funder — paid from the general pot"
            />
            <span className="form-helper">Optional — a paper trail for who's funding or decided this line, not a payment source.</span>
          </div>

          <RichTextEditor
            label="Notes"
            value={formData.notes}
            onChange={(val) => setFormData({ ...formData, notes: val })}
            placeholder="Any additional notes…"
            rows={3}
          />

          <RecordActivity entityType="budget_item" entityId={editingVendor?.id} heading="Comments" fullWidth />
        </>
      ) : (
        <>
          {/* Same field set as QuotesTracker's "+ New Quote" form — a
              vendor added from Budget → category becomes a quote too, just
              pre-filled with the category you're already in. */}
          <QuoteFormFields
            vendors={vendors}
            categories={categories}
            events={events}
            values={quoteValues}
            onChange={handleQuoteChange}
            vendorError={avErrors.vendor}
          />
          {avErrors.category && <span className="field-error">{avErrors.category}</span>}
          <p className="form-helper">This vendor is added as a quote. You'll add itemized costs on its own page next — accept it once you've decided to move forward.</p>
        </>
      )}
    </form>
  );
};

const AddPaymentForm: React.FC<{
  onSubmit: (payment: Payment) => void;
  onCancel: () => void;
  maxAmount: number;
  getCurrencySymbol: () => string;
  hideTitle?: boolean;
}> = ({ onSubmit, onCancel, maxAmount, getCurrencySymbol, hideTitle = false }) => {
  const { formatCurrency } = useSettings();
  const [formData, setFormData] = useState({
    amount: 0,
    date: localDayKey(),
    method: 'bank-transfer' as const,
    status: 'completed' as const,
    reference: '',
    notes: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      id: `payment-${Date.now()}`,
      ...formData
    });
  };

  return (
    <form className="add-payment-form" onSubmit={handleSubmit}>
      {!hideTitle && <h5>Add Payment</h5>}
      <div className="form-group">
        <label>Amount (max: {formatCurrency(maxAmount)})</label>
        <div className="currency-input-wrapper">
          <span className="currency-symbol">{getCurrencySymbol()}</span>
          <input
            type="text"
            value={formData.amount ? formData.amount.toLocaleString(getAppLocale()) : ''}
            onChange={(e) => {
              const value = e.target.value.replace(/[^\d.-]/g, '');
              const numValue = Number(value) || 0;
              if (numValue <= maxAmount) {
                setFormData({ ...formData, amount: numValue });
              }
            }}
            placeholder="0"
            required
          />
        </div>
      </div>
      <input
        type="date"
        value={formData.date}
        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
        required
      />
      <SearchableSelect
        value={formData.method}
        onChange={(value) => setFormData({ ...formData, method: value as any })}
        options={[
          { value: 'cash', label: 'Cash' },
          { value: 'bank-transfer', label: 'Bank Transfer' },
          { value: 'card', label: 'Card' },
          { value: 'mobile-money', label: 'Mobile Money' },
          { value: 'check', label: 'Check' },
        ]}
      />
      <SearchableSelect
        value={formData.status}
        onChange={(value) => setFormData({ ...formData, status: value as any })}
        options={[
          { value: 'completed', label: 'Completed' },
          { value: 'pending', label: 'Pending' },
          { value: 'failed', label: 'Failed' },
        ]}
      />
      <input
        type="text"
        placeholder="Reference (optional)"
        value={formData.reference}
        onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
      />
      <div className="form-actions">
        <button type="button" onClick={onCancel}>Cancel</button>
        <button type="submit">Add Payment</button>
      </div>
    </form>
  );
};

const CategoryModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  category: BudgetCategory | null;
  events: StoredEvent[];
  onSave: (category: BudgetCategory) => void;
  getCurrencySymbol: () => string;
}> = ({ isOpen, onClose, category, events, onSave, getCurrencySymbol }) => {
  const isEdit = category !== null;
  const [formData, setFormData] = useState({
    name: '',
    event_ids: [] as string[],
    planned_amount: 0,
    actual_amount: 0,
    vendor: '',
    payment_status: 'unpaid' as 'unpaid' | 'deposit-paid' | 'paid-in-full',
    notes: '',
    priority: 'must-have' as 'must-have' | 'nice-to-have' | 'cut-if-needed',
  });
  const [catErrors, setCatErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      if (category) {
        setFormData({
          name: category.name,
          event_ids: category.event_ids?.length ? category.event_ids : (category.event_id ? [category.event_id] : []),
          planned_amount: category.planned_amount,
          actual_amount: category.actual_amount,
          vendor: category.vendor || '',
          payment_status: category.payment_status || 'unpaid',
          notes: category.notes || '',
          priority: category.priority || 'must-have',
        });
      } else {
        setFormData({ name: '', event_ids: [], planned_amount: 0, actual_amount: 0, vendor: '', payment_status: 'unpaid', notes: '', priority: 'must-have' });
      }
    }
  }, [category, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!formData.name.trim()) errs.name = 'Required';
    if (!formData.planned_amount || formData.planned_amount <= 0) errs.planned_amount = 'Must be greater than 0';
    if (Object.keys(errs).length > 0) { setCatErrors(errs); return; }
    setCatErrors({});
    onSave({
      id: category?.id || `cat-${Date.now()}`,
      vendors: category?.vendors || [],
      name: formData.name,
      planned_amount: Number(formData.planned_amount) || 0,
      actual_amount: Number(formData.actual_amount) || 0,
      vendor: formData.vendor || undefined,
      payment_status: formData.payment_status,
      notes: formData.notes || undefined,
      priority: formData.priority,
      event_ids: formData.event_ids,
      event_id: formData.event_ids[0] || undefined,
    });
  };

  const sym = getCurrencySymbol();

  if (!isOpen) return null;

  return (
    <div className="inline-form-page">
      <div className="inline-form-header">
        <button className="btn-back" onClick={onClose}>
          <span className="bt-back-arrow">&larr;</span> Back
        </button>
        <div className="flex items-center gap-2">
          <h2>{isEdit ? 'Edit Budget Category' : 'Add Budget Category'}</h2>
          <button className="btn-primary" type="submit" form="category-modal-form">
            {isEdit ? 'Save Changes' : 'Add Category'}
          </button>
        </div>
      </div>
      <div className="inline-form-page">
      <form id="category-modal-form" onSubmit={handleSubmit} className="bt-form">
        <FormInput
          label="Category Name"
          type="text"
          value={formData.name}
          onChange={(e) => { setFormData({ ...formData, name: e.target.value }); if (catErrors.name) setCatErrors(prev => ({ ...prev, name: '' })); }}
          placeholder="e.g. Photography"
          required
          className="form-input"
          error={catErrors.name}
        />
        <div className="form-group">
          <label className="bt-form-label">Events</label>
          <TagMultiSelect
            values={formData.event_ids.map(eid => events.find(e => e.id === eid)?.title ?? eid)}
            options={events.map(ev => ev.title)}
            onChange={titles => {
              const ids = titles.map(t => events.find(ev => ev.title === t)?.id ?? t);
              setFormData(prev => ({ ...prev, event_ids: ids }));
            }}
            placeholder="All events"
            allowCreate={false}
          />
        </div>
        <div className="bt-form-grid-2">
          <div className="form-group">
            <label className="bt-form-label">Planned Amount ({sym}) <span className="form-required">*</span></label>
            <div className="currency-input-wrapper">
              <span className="currency-symbol">{sym}</span>
              <input
                type="text"
                value={formData.planned_amount ? formData.planned_amount.toLocaleString(getAppLocale()) : ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^\d]/g, '');
                  setFormData({ ...formData, planned_amount: Number(raw) || 0 });
                  if (catErrors.planned_amount) setCatErrors(prev => ({ ...prev, planned_amount: '' }));
                }}
                placeholder="0"
                required
                className={catErrors.planned_amount ? 'has-error' : undefined}
              />
            </div>
            {catErrors.planned_amount && <span className="field-error">{catErrors.planned_amount}</span>}
          </div>
          <div className="form-group">
            <label className="bt-form-label">Actual Amount ({sym})</label>
            <div className="currency-input-wrapper">
              <span className="currency-symbol">{sym}</span>
              <input
                type="text"
                value={formData.actual_amount ? formData.actual_amount.toLocaleString(getAppLocale()) : ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^\d]/g, '');
                  setFormData({ ...formData, actual_amount: Number(raw) || 0 });
                }}
                placeholder="0"
              />
            </div>
          </div>
        </div>
        <FormInput
          label="Vendor"
          type="text"
          value={formData.vendor}
          onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
          placeholder="Vendor name (optional)"
          className="form-input"
        />
        <div className="bt-form-grid-2">
          <div className="form-group">
            <label className="form-label">Payment Status</label>
            <SearchableSelect
              value={formData.payment_status}
              onChange={(value) => setFormData({ ...formData, payment_status: value as typeof formData.payment_status })}
              options={[
                { value: 'unpaid', label: 'Unpaid' },
                { value: 'deposit-paid', label: 'Deposit Paid' },
                { value: 'paid-in-full', label: 'Paid in Full' },
              ]}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Priority</label>
            <SearchableSelect
              value={formData.priority}
              onChange={(value) => setFormData({ ...formData, priority: value as typeof formData.priority })}
              options={[
                { value: 'must-have', label: 'Must Have' },
                { value: 'nice-to-have', label: 'Nice to Have' },
                { value: 'cut-if-needed', label: 'Cut If Needed' },
              ]}
            />
          </div>
        </div>
        <RichTextEditor
          label="Notes"
          value={formData.notes}
          onChange={(val) => setFormData({ ...formData, notes: val })}
          placeholder="Additional notes (optional)"
          rows={3}
        />
      </form>
      </div>
    </div>
  );
};

export default BudgetTracker;
